import React from "react";
import { useNavigate } from "react-router";

import { useAuth } from "~/context/authContext";
import { CallUiShell } from "~/context/CallUiShell";
import { buildCallerDetailsUrl, useSoftphoneUi } from "~/context/call/useSoftphoneUi";
import { useCallResolution } from "~/context/call/useCallResolution";
import type {
  CallActions,
  CallContextValue,
  ScreenedIncomingCall,
  HeldCallEntry,
} from "~/context/call/types";
export type { ScreenedIncomingCall, HeldCallEntry } from "~/context/call/types";
export type { CallActions, CallContextValue } from "~/context/call/types";
import {
  initialState,
  shouldSkipEndedCallHistoryForTransfer,
  type CallState,
} from "~/context/call/callState";
export type {
  CallState,
  SecurityEventRecord,
  AgentJoinedEventRecord,
  SocketStatus,
} from "~/context/call/callState";
import {
  agentsAreEqual,
  areRealtimeAgentsFresh,
  normalizePresenceAgent,
  readUserSipExtension,
  getOperatorDisplayName,
  matchesIncomingCallIdentity,
  isIncomingTerminalCallState,
  buildIncomingTerminalHistoryKey,
} from "~/context/call/helpers";
import {
  buildCustomerPhoneDirectory,
  resolveCallCustomerIdentity,
} from "~/services/calling/customerMatcher";
import { formatPhoneForDisplay, normalizePhoneNumber } from "~/utils/phone";
import { useSipLifecycle } from "~/context/call/useSipLifecycle";
import { useCallSocket } from "~/context/call/useCallSocket";
import { useCallQueue } from "~/context/call/useCallQueue";
import { useOperatorPresence } from "~/context/call/useOperatorPresence";
import { useConferenceControlRecovery } from "~/context/call/useConferenceControlRecovery";
import { useCallActions } from "~/context/call/useCallActions";
import { useSipTabCoordinator } from "~/context/call/useSipTabCoordinator";
import {
  CONNECTION_HEALTH_INITIAL_SNAPSHOT,
  createConnectionHealthRecorder,
  useConnectionHealth,
} from "~/context/call/useConnectionHealth";
import {
  registerAidCallConnectionHealthDebug,
  unregisterAidCallConnectionHealthDebug,
} from "~/context/call/connectionHealth/connectionHealthDebug";
import type {
  ConnectionFlapEventType,
  ConnectionHealthRecorder,
  ConnectionHealthSnapshot,
} from "~/context/call/connectionHealth/types";
import { getOperatorPresence, getUsers } from "~/services/userService";
import type { UserRecord } from "~/types/user";
import type { OutboundDialOptions } from "~/context/call/outboundCallLink";
import { appendCallHistory } from "~/services/callHistoryService";
import { getCustomerById } from "~/services/customerService";
import {
  appendCustomerActivity,
  createDiffField,
} from "~/services/customerInteractionService";
import { resolveSipDomain, resolveSipWssUrl } from "~/services/calling/config";
import { AppSocket } from "~/services/calling/socketClient";
import { SipPhone } from "~/services/calling/sipPhone";
import {
  disconnectActiveSipTransport,
  setActiveSipPhone,
  stopActiveSipPhone,
} from "~/services/calling/sipTeardown";
import {
  registerAidCallDebug,
  unregisterAidCallDebug,
} from "~/services/calling/callInitDebug";
import packageJson from "../../package.json";
import type {
  CallStatePayload,
} from "~/services/calling/types";

/** How often to re-check sidecar presence while the realtime agent feeds are silent. */
const PRESENCE_POLL_INTERVAL_MS = 20_000;

function normalizeSnapshotList(values?: string[] | null) {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);
}

function normalizeSnapshotDeviceStatus(
  value?: string | null
): "active" | "inactive" | null {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "active") return "active";
  if (normalized === "inactive") return "inactive";
  return null;
}

async function loadCustomerCallSnapshot(customerId: string) {
  const customer = await getCustomerById(customerId);
  const medicalConditions = normalizeSnapshotList(customer.medicalConditions);
  const currentMedications = normalizeSnapshotList(
    customer.currentMedications ?? customer.medications ?? null
  );
  const allergies = normalizeSnapshotList(customer.allergies);
  const bloodType = customer.bloodType?.trim() ? customer.bloodType.trim() : null;
  const bloodThinners =
    typeof customer.bloodThinners === "boolean" ? customer.bloodThinners : null;
  const emergencyContacts = (customer.emergencyContacts ?? []).map((contact) => ({
    id: contact.id,
    firstName: contact.firstName,
    lastName: contact.lastName,
    relationship: contact.relationship,
    phone: contact.phone,
    isPrimary: Boolean(contact.isPrimary),
    isKeyHolder: Boolean(contact.isKeyHolder),
    hasPropertyAccess: Boolean(contact.hasPropertyAccess),
  }));
  const deviceStatus = (customer.devices ?? []).map((device, index) => ({
    id: device.id,
    name:
      device.unitId?.trim() ||
      device.type?.trim() ||
      `Device ${index + 1}`,
    deviceType: device.type ?? null,
    unitId: device.unitId ?? null,
    telephone: device.phoneNumber ?? null,
    batteryLevel:
      typeof device.batteryLevel === "number"
        ? `${Math.max(0, Math.round(device.batteryLevel))}%`
        : null,
    signalStrength:
      typeof device.signalStrength === "number"
        ? String(device.signalStrength)
        : null,
    lastCheckIn: device.lastCheckIn ?? null,
    lastTestDate: device.lastServiceDate ?? null,
    status: normalizeSnapshotDeviceStatus(device.status),
  }));

  return {
    medicalConditions,
    currentMedications,
    allergies,
    bloodType,
    bloodThinners,
    emergencyContacts,
    deviceStatus,
  };
}

const CallContext = React.createContext<CallContextValue | null>(null);

function isLiveCallStatus(status: CallState["callStatus"]) {
  return (
    status === "ringing" ||
    status === "dialing" ||
    status === "active" ||
    status === "held"
  );
}

function formatCallStatusLabel(status: CallState["callStatus"]) {
  switch (status) {
    case "ringing":
      return "Ringing";
    case "dialing":
      return "Dialing";
    case "active":
      return "Active";
    case "held":
      return "On Hold";
    case "ended":
      return "Ended";
    default:
      return "Idle";
  }
}

const HOLD_LOCKOUT_MESSAGE = "Complete your held call before taking a new one.";
const SIP_NOT_REGISTERED_MESSAGE = "SIP not registered";

function normalizeRoleValue(role?: string | null) {
  return (role ?? "").trim().toUpperCase().replace(/[\s-]+/g, "_");
}

function startsOfflineByRole(role?: string | null) {
  const normalizedRole = normalizeRoleValue(role);
  if (!normalizedRole) return false;
  return normalizedRole === "GENERAL_ADMIN" || normalizedRole === "SUPER_ADMIN";
}

function readUserPermissionLevel(user: unknown) {
  if (!user || typeof user !== "object") return null;
  const permissionLevel = (user as { permissionLevel?: string | null }).permissionLevel;
  return typeof permissionLevel === "string" ? permissionLevel : null;
}

function readEffectiveRole(user: unknown) {
  if (!user || typeof user !== "object") return null;
  const record = user as { role?: string | null; permissionLevel?: string | null };
  const role = typeof record.role === "string" ? record.role.trim() : "";
  if (role) return role;
  const permissionLevel =
    typeof record.permissionLevel === "string" ? record.permissionLevel.trim() : "";
  return permissionLevel || null;
}

export function CallProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { isAuthenticated, isHydrated, token, user } = useAuth();
  const effectiveRole = readEffectiveRole(user);
  const userId = user?.id;
  const userEmail = user?.email;
  const userName = user?.name;
  const userSurname = user?.surname;
  const userExtensionId = user?.extensionId;
  const userSipExtension = readUserSipExtension(user);
  const [state, setState] = React.useState<CallState>(initialState);
  const [manualDisconnect, setManualDisconnect] = React.useState(true);
  const sipTabCoordinator = useSipTabCoordinator({
    enabled:
      isAuthenticated &&
      isHydrated &&
      Boolean(effectiveRole) &&
      Boolean(userId) &&
      !manualDisconnect,
    scope: userId,
    sipStatus: state.sipStatus,
  });

  // SIP
  const sipRef = React.useRef<SipPhone | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const sipIdentityRef = React.useRef<{
    extension: string | null;
    sipUsername: string | null;
  }>({ extension: null, sipUsername: null });
  const sipSessionGenerationRef = React.useRef(0);
  const userOptedOnlineRef = React.useRef(false);
  const sipLifecycleStartedRef = React.useRef(false);

  // Socket & operator presence
  const socketRef = React.useRef<AppSocket | null>(null);
  /** `Date.now()` of the last realtime agent roster update; 0 when none has arrived. */
  const realtimeAgentsUpdatedAtRef = React.useRef(0);
  const operatorPresenceSocketRef = React.useRef<WebSocket | null>(null);
  const operatorPresenceKeepAliveRef = React.useRef<number | null>(null);
  const operatorPresenceReconnectRef = React.useRef<number | null>(null);
  const operatorPresenceShouldReconnectRef = React.useRef(false);
  const initializeSocketRef = React.useRef<(() => void) | null>(null);
  const initializeOperatorPresenceRef = React.useRef<(() => void) | null>(null);
  const initializeSipRef = React.useRef<(() => Promise<void>) | null>(null);

  // Conference recovery
  const conferenceNoAnswerTimeoutRef = React.useRef<number | null>(null);
  const conferenceControlRecoveryUntilRef = React.useRef<number>(0);

  // Supervise auto-accept (mode set before sidecar Originate; consumed on INVITE)
  const pendingSuperviseRef = React.useRef<
    import("~/services/calling/types").PendingSupervise | null
  >(null);
  const pendingSuperviseTimeoutRef = React.useRef<number | null>(null);
  const trySuperviseAutoAcceptRef = React.useRef<() => boolean>(() => false);

  // Snapshot & reconnect
  const previousStateRef = React.useRef<CallState>(initialState);
  const stateSnapshotRef = React.useRef<CallState>(initialState);
  const reconnectingRef = React.useRef(false);
  const handledIncomingTerminalKeysRef = React.useRef<Set<string>>(new Set());
  const connectionHealthRecorderRef = React.useRef<ConnectionHealthRecorder>(
    createConnectionHealthRecorder(() => undefined)
  );
  const canTakeCallsRef = React.useRef(false);
  const connectionHealthSnapshotRef = React.useRef<ConnectionHealthSnapshot>(
    CONNECTION_HEALTH_INITIAL_SNAPSHOT
  );
  const connectionHealthRecordFlapRef = React.useRef<
    (eventType: ConnectionFlapEventType) => void
  >(() => undefined);

  React.useEffect(() => {
    stateSnapshotRef.current = state;
  }, [state]);

  const currentAgent = React.useMemo(() => {
    if (!user) return null;
    const name = [user.name, user.surname].filter(Boolean).join(" ").trim();
    return {
      id: user.id,
      name: name || user.email,
      status: "online" as const,
      sipExtension: user.extensionId ?? null,
      sipUri: null,
      profileImageUrl: user.profilePicUrl ?? null,
    };
  }, [user]);

  const updateState = React.useCallback(
    (next: Partial<CallState>) => {
      setState((prev) => {
        let hasChange = false;
        for (const key of Object.keys(next) as (keyof CallState)[]) {
          if (prev[key] !== next[key]) {
            hasChange = true;
            break;
          }
        }
        if (!hasChange) return prev;

        return {
          ...prev,
          ...next,
        };
      });
    },
    []
  );

  const reportCallError = React.useCallback(
    (message: string) => updateState({ lastError: message }),
    [updateState]
  );

  const scheduleOperatorPresenceReconnect = React.useCallback(() => {
    initializeOperatorPresenceRef.current?.();
  }, []);

  const operatorDisplayName = React.useMemo(
    () => getOperatorDisplayName(user),
    [user]
  );

  const appendCallActivity = React.useCallback(
    (input: {
      message: string;
      customerId?: string | null;
      diff?: ReturnType<typeof createDiffField>[];
      timestampMs?: number;
    }) => {
      const targetCustomerId = input.customerId ?? stateSnapshotRef.current.customerId;
      if (!targetCustomerId) return;
      const diff = (input.diff ?? []).filter(
        (item): item is NonNullable<ReturnType<typeof createDiffField>> =>
          Boolean(item)
      );
      appendCustomerActivity(targetCustomerId, {
        message: input.message,
        ...(diff.length > 0 ? { diff } : {}),
        operatorName: operatorDisplayName,
        source: "ui",
        ...(input.timestampMs ? { timestampMs: input.timestampMs } : {}),
      });
    },
    [operatorDisplayName]
  );

  const appendIncomingTerminalHistory = React.useCallback(
    (payload: Pick<CallStatePayload, "callUuid" | "callId" | "id" | "state">) => {
      if (!isIncomingTerminalCallState(payload.state)) return;

      const snapshot = stateSnapshotRef.current;
      const canFallbackToOnlyIncoming =
        !payload.callUuid &&
        !payload.callId &&
        !payload.id &&
        snapshot.incomingCalls.length === 1;
      const matchedIncoming =
        snapshot.incomingCalls.find((call) =>
          matchesIncomingCallIdentity(call, payload)
        ) ??
        (canFallbackToOnlyIncoming ? snapshot.incomingCalls[0] : null);
      if (!matchedIncoming) return;

      const historyKey = buildIncomingTerminalHistoryKey(payload, matchedIncoming);
      if (!historyKey) return;

      const handledKeys = handledIncomingTerminalKeysRef.current;
      if (handledKeys.has(historyKey)) return;
      handledKeys.add(historyKey);
      if (handledKeys.size > 500) {
        handledKeys.clear();
        handledKeys.add(historyKey);
      }

      const operatorName = operatorDisplayName;
      const operatorNames = operatorName ? [operatorName] : null;
      const endedAtMs = Date.now();

      appendCallHistory({
        id: `call-missed-${historyKey}`,
        customerId: matchedIncoming.customerId ?? snapshot.customerId ?? null,
        callUuid:
          matchedIncoming.callUuid ??
          matchedIncoming.callId ??
          payload.callUuid ??
          payload.callId ??
          payload.id ??
          snapshot.callId ??
          snapshot.callUuid ??
          null,
        callReference: matchedIncoming.service ?? snapshot.callReference ?? null,
        callerNumber: matchedIncoming.callerNumber ?? snapshot.callerNumber ?? null,
        customerName: matchedIncoming.customerName ?? snapshot.customerName ?? null,
        operatorName,
        operatorNames,
        telephoneLine: matchedIncoming.service ?? snapshot.callReference ?? null,
        description: matchedIncoming.description ?? "Missed incoming call",
        direction: "incoming",
        startedAtMs: null,
        endedAtMs,
        durationSeconds: 0,
      });
    },
    [operatorDisplayName]
  );

  const onSipIdentityChange = React.useCallback(() => {
    const socketStatus = stateSnapshotRef.current.socketStatus;
    if (socketStatus !== "connected" && socketStatus !== "connecting") {
      initializeSocketRef.current?.();
    }
    initializeOperatorPresenceRef.current?.();
  }, []);

  const onSipSessionReady = React.useCallback(() => {
    initializeSocketRef.current?.();
  }, []);

  const { initializeSip, trySuperviseAutoAccept } = useSipLifecycle({
    ownsSip: sipTabCoordinator.isLeader,
    sipRef,
    audioRef,
    sipIdentityRef,
    sipSessionGenerationRef,
    pendingSuperviseRef,
    updateState,
    setState,
    user,
    onSipIdentityChange,
    onSipSessionReady,
    connectionHealthRecorderRef,
  });

  React.useEffect(() => {
    trySuperviseAutoAcceptRef.current = trySuperviseAutoAccept;
  }, [trySuperviseAutoAccept]);

  const connectionHealthEnabled =
    isAuthenticated &&
    isHydrated &&
    Boolean(effectiveRole) &&
    !manualDisconnect;

  const connectionHealth = useConnectionHealth({
    enabled: connectionHealthEnabled,
    socketStatus: state.socketStatus,
    sipStatus:
      !sipTabCoordinator.isLeader && sipTabCoordinator.canRequestLeader
        ? "registered"
        : state.sipStatus,
    socketRef,
    extension:
      sipIdentityRef.current.extension ??
      userSipExtension ??
      userExtensionId ??
      null,
    recorderRef: connectionHealthRecorderRef,
    canTakeCallsRef,
    recordFlapRef: connectionHealthRecordFlapRef,
  });

  React.useEffect(() => {
    connectionHealthSnapshotRef.current = connectionHealth;
  }, [connectionHealth]);

  const callQueue = useCallQueue({
    isAuthenticated,
    userSipExtension,
    userExtensionId,
    sipIdentityRef,
    setState,
    reportError: reportCallError,
  });

  const { initializeSocket } = useCallSocket({
    socketRef,
    token: token ?? null,
    sipIdentityRef,
    realtimeAgentsUpdatedAtRef,
    updateState,
    setState,
    userExtensionId,
    userSipExtension,
    userId,
    userEmail,
    onIncomingTerminalState: appendIncomingTerminalHistory,
    onQueueResyncRequested: callQueue.scheduleQueueRefresh,
    onQueueClaimed: callQueue.handleQueueClaimed,
    onQueueClaimReleased: callQueue.handleQueueClaimReleased,
    pendingSuperviseRef,
    pendingSuperviseTimeoutRef,
    trySuperviseAutoAcceptRef,
    connectionHealthRecorderRef,
  });

  const { initializeOperatorPresence, stopOperatorPresence } =
    useOperatorPresence({
      operatorPresenceSocketRef,
      operatorPresenceKeepAliveRef,
      operatorPresenceReconnectRef,
      operatorPresenceShouldReconnectRef,
      realtimeAgentsUpdatedAtRef,
      sipIdentityRef,
      updateState,
      setState,
      isAuthenticated,
      userExtensionId,
      userSipExtension,
      userId,
      userEmail,
      userName,
      userSurname,
      scheduleReconnect: scheduleOperatorPresenceReconnect,
    });

  React.useEffect(() => {
    initializeSocketRef.current = initializeSocket;
  }, [initializeSocket]);

  React.useEffect(() => {
    initializeOperatorPresenceRef.current = initializeOperatorPresence;
  }, [initializeOperatorPresence]);

  React.useEffect(() => {
    initializeSipRef.current = initializeSip;
  }, [initializeSip]);

  const ensureSipDomain = React.useCallback(() => {
    const connectedDomain = sipRef.current?.getConnectLoggingContext()?.sipDomain;
    if (connectedDomain) return connectedDomain;

    if (state.sipDomain) return state.sipDomain;
    const sipWssUrl = resolveSipWssUrl();
    const domain = resolveSipDomain(sipWssUrl ?? undefined);
    if (domain) updateState({ sipDomain: domain });
    return domain;
  }, [state.sipDomain, updateState]);

  const unhold = React.useCallback(async () => {
    try {
      await sipRef.current?.unhold();
      updateState({
        isOnHold: false,
        callStatus: "active",
        backgroundHeldCallUuid: null,
        backgroundHeldCallReference: null,
        heldCallerNumber: null,
        consultNumber: null,
        consultStatus: null,
      });
      return;
    } catch (error) {
      const latest = stateSnapshotRef.current;
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Unable to resume call from hold.";
      updateState({
        isOnHold: false,
        callStatus:
          latest.callStatus === "idle" || latest.callStatus === "ended"
            ? latest.callStatus
            : "active",
        lastError: message,
      });
      if (
        latest.sipStatus !== "registered" ||
        !latest.conferenceRoom ||
        (latest.callStatus !== "held" && latest.callStatus !== "idle")
      ) {
        return;
      }
      const sipDomain = ensureSipDomain();
      if (!sipDomain) return;
      try {
        updateState({
          callStatus: "dialing",
          callDirection: "outgoing",
        });
        await sipRef.current?.joinRoom(latest.conferenceRoom, sipDomain);
      } catch (joinError) {
        const joinMessage =
          joinError instanceof Error && joinError.message
            ? joinError.message
            : "Unable to recover conference call.";
        updateState({ lastError: joinMessage });
      }
    }
  }, [ensureSipDomain, sipRef, stateSnapshotRef, updateState]);

  const conferenceRecovery = useConferenceControlRecovery({
    conferenceNoAnswerTimeoutRef,
    conferenceControlRecoveryUntilRef,
    stateSnapshotRef,
    sipRef,
    ensureSipDomain,
    unhold,
    updateState,
    state,
  });

  const callActionsResult = useCallActions({
    state,
    setState,
    updateState,
    sipRef,
    socketRef,
    sipIdentityRef,
    stateSnapshotRef,
    pendingSuperviseRef,
    pendingSuperviseTimeoutRef,
    currentAgent,
    user,
    ensureSipDomain,
    unhold,
    reportError: reportCallError,
    conferenceRecovery,
    canTakeCallsRef,
  });

  const disconnectSipOnly = React.useCallback(async () => {
    sipSessionGenerationRef.current += 1;
    disconnectActiveSipTransport();

    if (sipRef.current) {
      await Promise.race([
        sipRef.current.stop(),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, 3000);
        }),
      ]);
      setActiveSipPhone(null);
    }

    updateState({ sipStatus: "offline" });
  }, [updateState]);

  const disconnectAll = React.useCallback(async () => {
    sipSessionGenerationRef.current += 1;
    disconnectActiveSipTransport();
    stopOperatorPresence(false);
    socketRef.current?.disconnect();
    updateState({
      sipStatus: "offline",
      socketStatus: "disconnected",
    });

    if (sipRef.current) {
      await Promise.race([
        sipRef.current.stop(),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, 3000);
        }),
      ]);
      setActiveSipPhone(null);
    }
  }, [stopOperatorPresence, updateState]);

  const disconnectAllRef = React.useRef(disconnectAll);
  disconnectAllRef.current = disconnectAll;
  const disconnectSipOnlyRef = React.useRef(disconnectSipOnly);
  disconnectSipOnlyRef.current = disconnectSipOnly;

  React.useEffect(() => {
    const onPageHide = () => {
      disconnectActiveSipTransport();
    };

    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  React.useEffect(() => {
    if (import.meta.env.DEV) return;
    if (!isLiveCallStatus(state.callStatus)) return;

    // Browsers show a generic confirmation dialog; custom text is ignored.
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [state.callStatus]);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (!effectiveRole) return;

    const nextManualDisconnect = startsOfflineByRole(effectiveRole);
    setManualDisconnect((prev) =>
      prev === nextManualDisconnect ? prev : nextManualDisconnect
    );

    if (!nextManualDisconnect) {
      userOptedOnlineRef.current = true;
    }
  }, [effectiveRole, isAuthenticated, user?.id, user?.role]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      userOptedOnlineRef.current = false;
      sipLifecycleStartedRef.current = false;
      setManualDisconnect(false);
      sipIdentityRef.current = {
        extension: null,
        sipUsername: null,
      };
      setState(initialState);

      const latest = stateSnapshotRef.current;
      const isAlreadyDisconnected =
        latest.sipStatus === "offline" && latest.socketStatus === "disconnected";

      if (!isAlreadyDisconnected) {
        void disconnectAll();
      }

      return;
    }

    if (!isHydrated) return;

    if (!effectiveRole) {
      return;
    }

    if (!userOptedOnlineRef.current) {
      return;
    }

    if (manualDisconnect) {
      return;
    }

    sipLifecycleStartedRef.current = true;
    void initializeSipRef.current?.();
    initializeOperatorPresenceRef.current?.();

    return () => {
      if (!sipLifecycleStartedRef.current) return;

      sipLifecycleStartedRef.current = false;
      // Do not tear down Socket.IO here — disconnectAll was causing identify-then-drop loops.
      void disconnectSipOnlyRef.current();
    };
  }, [
    effectiveRole,
    isAuthenticated,
    isHydrated,
    manualDisconnect,
    sipTabCoordinator.isLeader,
  ]);

  React.useEffect(() => {
    if (!isHydrated) return;
    if (!isAuthenticated) return;
    if (!effectiveRole) return;
    if (!userOptedOnlineRef.current) return;
    if (manualDisconnect) return;
    if (state.sipStatus !== "registered") return;
    if (
      state.socketStatus === "connected" ||
      state.socketStatus === "connecting"
    ) {
      return;
    }
    if (!sipIdentityRef.current.extension) return;

    initializeSocket();
  }, [
    initializeSocket,
    isAuthenticated,
    isHydrated,
    manualDisconnect,
    state.sipStatus,
    state.socketStatus,
    effectiveRole,
  ]);

  React.useEffect(() => {
    if (!isHydrated) return;
    if (!isAuthenticated) return;
    if (!effectiveRole) return;
    if (!userOptedOnlineRef.current) return;
    if (manualDisconnect) return;
    if (!sipIdentityRef.current.extension) return;
    if (state.socketStatus === "connected" || state.socketStatus === "connecting") {
      return;
    }

    const timer = window.setTimeout(() => {
      if (!sipIdentityRef.current.extension) return;
      initializeSocket();
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    effectiveRole,
    initializeSocket,
    isAuthenticated,
    isHydrated,
    manualDisconnect,
    state.socketStatus,
  ]);

  // Seed and refresh the agent roster from sidecar presence whenever the realtime feeds
  // (dashboard socket / operator WS) have not delivered recently. Availability never comes
  // from User.connectionStatus, which stays "Online" for anyone who just closed their tab.
  React.useEffect(() => {
    if (!isAuthenticated) return;

    let mounted = true;

    const syncPresence = async () => {
      if (!mounted) return;
      if (areRealtimeAgentsFresh(realtimeAgentsUpdatedAtRef.current)) return;

      try {
        const [snapshot, users] = await Promise.all([
          getOperatorPresence(),
          getUsers().catch(() => [] as UserRecord[]),
        ]);
        if (!mounted) return;
        if (areRealtimeAgentsFresh(realtimeAgentsUpdatedAtRef.current)) return;

        const profilesByUserId = new Map(users.map((user) => [user.id, user]));
        const nextAgents = snapshot.data.map((record) =>
          normalizePresenceAgent(record, profilesByUserId.get(record.userId))
        );
        setState((prev) =>
          agentsAreEqual(prev.agents, nextAgents)
            ? prev
            : { ...prev, agents: nextAgents }
        );
      } catch {
        // Presence is advisory here; realtime feeds remain the primary source.
      }
    };

    void syncPresence();
    const timer = window.setInterval(syncPresence, PRESENCE_POLL_INTERVAL_MS);

    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [isAuthenticated, setState]);

  React.useEffect(() => {
    if (state.callStatus !== "active" || !state.callStartedAt) return;

    const timer = window.setInterval(() => {
      setState((prev) => {
        if (!prev.callStartedAt) return prev;
        const elapsedSeconds = Math.floor(
          (Date.now() - prev.callStartedAt) / 1000
        );
        return { ...prev, callElapsedSeconds: elapsedSeconds };
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [state.callStatus, state.callStartedAt]);

  const appendEndedCallHistory = React.useCallback(
    async (previous: CallState, endedAtMs: number) => {
      const wasLive = isLiveCallStatus(previous.callStatus);
      const endedNow = state.callStatus === "ended";
      const activityCustomerId = state.customerId ?? previous.customerId ?? null;

      if (!previous.isOnHold && state.isOnHold && activityCustomerId) {
        appendCallActivity({
          customerId: activityCustomerId,
          message: "Call placed on hold",
          diff: [
            createDiffField(
              "Call Status",
              formatCallStatusLabel(previous.callStatus),
              "On Hold"
            ),
          ],
        });
      }

      if (
        previous.isOnHold &&
        !state.isOnHold &&
        state.callStatus !== "ended" &&
        activityCustomerId
      ) {
        appendCallActivity({
          customerId: activityCustomerId,
          message: "Call resumed from hold",
          diff: [
            createDiffField(
              "Call Status",
              "On Hold",
              formatCallStatusLabel(state.callStatus)
            ),
          ],
        });
      }

      if (activityCustomerId && state.participants.length > 0) {
        const previousParticipantIds = new Set(
          previous.participants.map((participant) => participant.id)
        );
        const joinedParticipants = state.participants.filter((participant) => {
          if (!participant.id || participant.id === userId) return false;
          return !previousParticipantIds.has(participant.id);
        });

        if (joinedParticipants.length > 0) {
          const joinedNames = joinedParticipants
            .map((participant) => participant.name.trim())
            .filter(Boolean);
          appendCallActivity({
            customerId: activityCustomerId,
            message:
              joinedNames.length === 1
                ? `Operator dialed in: ${joinedNames[0]}`
                : "Operators dialed in",
            diff: [
              createDiffField(
                "Operators",
                "",
                joinedNames.length > 0
                  ? joinedNames.join(", ")
                  : String(joinedParticipants.length)
              ),
            ],
          });
        }
      }

      const outboundConnected =
        previous.callStatus === "dialing" &&
        previous.callDirection === "outgoing" &&
        !previous.conferenceRoom &&
        state.callStatus === "active";

      if (outboundConnected && activityCustomerId) {
        appendCallActivity({
          customerId: activityCustomerId,
          message: "Outbound call answered",
          diff: [
            createDiffField("Call Status", "Dialing", "Answered"),
            createDiffField(
              "Caller Number",
              "",
              state.callerNumber ?? previous.callerNumber ?? ""
            ),
          ],
        });
      }

      if (wasLive && endedNow) {
        if (shouldSkipEndedCallHistoryForTransfer(previous)) {
          return;
        }

        const endedAtMs = Date.now();
        const startedAtMs = previous.callStartedAt ?? null;
        const elapsedSecondsFromStart = startedAtMs
          ? Math.max(0, Math.floor((endedAtMs - startedAtMs) / 1000))
          : 0;
        const durationSeconds = Math.max(
          previous.callElapsedSeconds,
          elapsedSecondsFromStart
        );
        const direction =
          previous.callDirection === "outgoing" ? "outgoing" : "incoming";
        const operatorName = operatorDisplayName;
        const participantNames = previous.participants
          .map((participant) => participant.name.trim())
          .filter(Boolean);
        const operatorNames = [operatorName, ...participantNames]
          .filter(Boolean)
          .filter(
            (value, index, array): value is string => array.indexOf(value) === index
          );

        const idSeed =
          previous.callUuid ??
          previous.callId ??
          previous.callReference ??
          previous.callerNumber ??
          `${endedAtMs}`;
        const normalizedSeed = idSeed
          .replace(/[^a-z0-9_-]/gi, "")
          .slice(-24);

        let customerSnapshot: Awaited<
          ReturnType<typeof loadCustomerCallSnapshot>
        > | null = null;
        if (previous.customerId) {
          try {
            customerSnapshot = await loadCustomerCallSnapshot(previous.customerId);
          } catch {
            customerSnapshot = null;
          }
        }

        appendCallHistory({
          id: `call-${endedAtMs}-${normalizedSeed || "entry"}`,
          customerId: previous.customerId ?? null,
          callUuid: previous.callUuid ?? previous.callId ?? null,
          callReference: previous.callReference ?? null,
          callerNumber: previous.callerNumber ?? null,
          customerName: previous.customerName ?? null,
          operatorName,
          operatorNames: operatorNames.length > 0 ? operatorNames : null,
          telephoneLine: previous.callReference ?? null,
          description: null,
          direction,
          startedAtMs,
          endedAtMs,
          durationSeconds,
          customerSnapshot,
        });
      }
    },
    [user]
  );

  React.useEffect(() => {
    const previous = previousStateRef.current;
    const wasLive = ["dialing", "ringing", "active", "held"].includes(
      previous.callStatus
    );
    const endedNow = state.callStatus === "ended";

    if (wasLive && endedNow) {
      const endedAtMs = Date.now();
      void appendEndedCallHistory(previous, endedAtMs);
    }

    previousStateRef.current = state;
  }, [appendEndedCallHistory, state]);

  const reconnect = React.useCallback(() => {
    const wasManualDisconnect = manualDisconnect;
    userOptedOnlineRef.current = true;
    setManualDisconnect(false);

    // Going from offline → online: the manualDisconnect effect already starts SIP.
    if (wasManualDisconnect) return;

    if (reconnectingRef.current) return;

    reconnectingRef.current = true;
    void disconnectAll()
      .then(() => {
        initializeSip();
        initializeOperatorPresence();
      })
      .finally(() => {
        window.setTimeout(() => {
          reconnectingRef.current = false;
        }, 750);
      });
  }, [disconnectAll, initializeOperatorPresence, initializeSip, manualDisconnect]);

  const disconnect = React.useCallback(async () => {
    userOptedOnlineRef.current = false;
    await disconnectAll();
    setManualDisconnect(true);
  }, [disconnectAll]);

  const {
    call,
    answer,
    hangup,
    sendDtmf,
    mute,
    unmute,
    hold,
    unhold: unholdAction,
    swap,
    transferToAgent,
    transferBlind,
    startConferenceWithAgents,
    joinConferenceRoom,
    removeParticipant,
    dismissInvite,
    rejectPendingInvitation,
    dismissCallTakenNotice,
    dismissBargePromotionNotice,
    dismissBargeTransferNotice,
    clearCallError,
    canAcceptIncomingCall,
    canAcceptQueueCall,
    resolveAnswerIncomingId,
    setCallMetadata,
    supervise,
    inviteOperator,
  } = callActionsResult;

  const requestPrimaryCallingTab = React.useCallback(() => {
    sipTabCoordinator.requestLeaderFocus();
    reportCallError(
      "Call controls are active in another AidCall tab. Continue the call there."
    );
  }, [reportCallError, sipTabCoordinator.requestLeaderFocus]);

  const answerWithSipOwnership = React.useCallback(
    async (incomingId?: string) => {
      if (sipTabCoordinator.isLeader) {
        await answer(incomingId);
        return;
      }

      requestPrimaryCallingTab();
    },
    [answer, requestPrimaryCallingTab, sipTabCoordinator.isLeader]
  );

  const rejectWithSipOwnership = React.useCallback(
    async (incomingId?: string) => {
      if (!sipTabCoordinator.isLeader) {
        requestPrimaryCallingTab();
        return;
      }

      await rejectPendingInvitation(incomingId);
    },
    [
      rejectPendingInvitation,
      requestPrimaryCallingTab,
      sipTabCoordinator.isLeader,
    ]
  );

  const hangupWithSipOwnership = React.useCallback(async () => {
    if (!sipTabCoordinator.isLeader) {
      requestPrimaryCallingTab();
      return;
    }

    await hangup();
  }, [hangup, requestPrimaryCallingTab, sipTabCoordinator.isLeader]);

  const callWithSipOwnership = React.useCallback(
    async (target: string, options?: OutboundDialOptions) => {
      if (!sipTabCoordinator.isLeader) {
        requestPrimaryCallingTab();
        return;
      }

      await call(target, options);
    },
    [call, requestPrimaryCallingTab, sipTabCoordinator.isLeader]
  );

  const unholdWithSipOwnership = React.useCallback(async () => {
    if (!sipTabCoordinator.isLeader) {
      requestPrimaryCallingTab();
      return;
    }

    await unholdAction();
  }, [requestPrimaryCallingTab, sipTabCoordinator.isLeader, unholdAction]);

  const canAcceptIncomingAcrossTabs = React.useCallback(
    (incomingId: string) => {
      if (sipTabCoordinator.isLeader) {
        return canAcceptIncomingCall(incomingId);
      }

      return sipTabCoordinator.canRequestLeader;
    },
    [
      canAcceptIncomingCall,
      sipTabCoordinator.canRequestLeader,
      sipTabCoordinator.isLeader,
    ]
  );

  const canAcceptQueueAcrossTabs = React.useCallback(
    (queueUniqueId: string) => {
      if (sipTabCoordinator.isLeader) {
        return canAcceptQueueCall(queueUniqueId);
      }

      return sipTabCoordinator.canRequestLeader;
    },
    [
      canAcceptQueueCall,
      sipTabCoordinator.canRequestLeader,
      sipTabCoordinator.isLeader,
    ]
  );

  const softphone = useSoftphoneUi({
    isAuthenticated,
    state: {
      incomingCalls: state.incomingCalls,
      callDirection: state.callDirection,
      callStatus: state.callStatus,
      callUuid: state.callUuid,
      callId: state.callId,
      callReference: state.callReference,
      callerNumber: state.callerNumber,
      customerName: state.customerName,
      isOnHold: state.isOnHold,
      backgroundHeldCallUuid: state.backgroundHeldCallUuid,
      backgroundHeldCallReference: state.backgroundHeldCallReference,
    },
    setCallMetadata,
    answer: answerWithSipOwnership,
    rejectPendingInvitation: rejectWithSipOwnership,
    hangup: hangupWithSipOwnership,
    call: callWithSipOwnership,
    unhold: unholdWithSipOwnership,
    reportError: reportCallError,
  });

  const callResolution = useCallResolution({
    state,
    stateSnapshotRef,
    activeCustomerId: softphone.activeCustomerId,
    hangup: hangupWithSipOwnership,
    updateState,
    operatorId: userId,
    operatorName: operatorDisplayName,
  });

  const actionableIncomingCalls = React.useMemo(
    () =>
      softphone.screenedIncomingCalls.filter((call) =>
        canAcceptIncomingAcrossTabs(call.id)
      ),
    [canAcceptIncomingAcrossTabs, softphone.screenedIncomingCalls]
  );

  const customerPhoneDirectory = React.useMemo(
    () => buildCustomerPhoneDirectory(softphone.customers),
    [softphone.customers]
  );

  const screenedQueueableCalls = React.useMemo(() => {
    return state.queueableCalls.map((call) => {
      const claimStatus = state.queueClaimByUniqueId[call.uniqueId] ?? "available";
      const identity = resolveCallCustomerIdentity(call, customerPhoneDirectory);

      return {
        ...call,
        ...identity,
        displayNumber: formatPhoneForDisplay(call.callerNumber),
        claimStatus,
      };
    });
  }, [
    customerPhoneDirectory,
    state.queueClaimByUniqueId,
    state.queueableCalls,
  ]);

  const actions = React.useMemo<CallActions>(
    () => ({
      reconnect,
      disconnect,
      call: async (target: string, options?: OutboundDialOptions) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        await call(target, options);
        const snapshot = stateSnapshotRef.current;
        const targetCustomerId =
          options?.customerId ?? snapshot.customerId ?? softphone.activeCustomerId;
        if (!targetCustomerId) return;
        appendCallActivity({
          customerId: targetCustomerId,
          message: "Outbound call started",
          diff: [
            createDiffField("Call Direction", "", "Outbound"),
            createDiffField("Caller Number", "", target),
          ],
        });
      },
      answer: answerWithSipOwnership,
      hangup: async () => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await hangup();
      },
      sendDtmf: async (tone: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await sendDtmf(tone);
      },
      mute: () => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        mute();
      },
      unmute: () => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        unmute();
      },
      hold: async () => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await hold();
      },
      unhold: async () => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await unholdAction();
      },
      swap: async () => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await swap();
      },
      transferToAgent: async (agentId: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        const snapshot = stateSnapshotRef.current;
        const targetAgent = snapshot.agents.find((agent) => agent.id === agentId);
        await transferToAgent(agentId);
        const targetCustomerId = snapshot.customerId ?? softphone.activeCustomerId;
        if (!targetCustomerId) return;
        appendCallActivity({
          customerId: targetCustomerId,
          message: targetAgent?.name
            ? `Call transferred to ${targetAgent.name}`
            : "Call transferred to operator",
          diff: [
            createDiffField("Transfer Type", "", "Agent"),
            createDiffField("Transfer Target", "", targetAgent?.name ?? agentId),
          ],
        });
      },
      transferBlind: async (target: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        const snapshot = stateSnapshotRef.current;
        await transferBlind(target);
        const targetCustomerId = snapshot.customerId ?? softphone.activeCustomerId;
        if (!targetCustomerId) return;
        appendCallActivity({
          customerId: targetCustomerId,
          message: `Call transferred to ${target}`,
          diff: [
            createDiffField("Transfer Type", "", "Blind"),
            createDiffField("Transfer Target", "", target),
          ],
        });
      },
      startConferenceWithAgents: async (agentIds: string[]) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await startConferenceWithAgents(agentIds);
      },
      removeParticipant: (participantId: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        removeParticipant(participantId);
      },
      joinConferenceRoom: async (room: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await joinConferenceRoom(room);
      },
      dismissInvite,
      rejectPendingInvitation: async (incomingId?: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await rejectPendingInvitation(incomingId);
      },
      dismissCallTakenNotice,
      dismissBargePromotionNotice,
      dismissBargeTransferNotice,
      clearCallError,
      canAcceptIncomingCall: canAcceptIncomingAcrossTabs,
      canAcceptQueueCall: canAcceptQueueAcrossTabs,
      answerQueueCall: async (queueUniqueId: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        const queueCall = screenedQueueableCalls.find(
          (callItem) => callItem.uniqueId === queueUniqueId
        );
        const incomingId = resolveAnswerIncomingId(queueUniqueId);
        await answer(incomingId);

        const snapshot = stateSnapshotRef.current;
        const normalizedCaller =
          normalizePhoneNumber(queueCall?.callerNumber ?? snapshot.callerNumber) ||
          queueCall?.callerNumber ||
          snapshot.callerNumber;
        const customerId =
          queueCall?.customerId ??
          snapshot.customerId ??
          softphone.activeCustomerId;
        const customerName =
          queueCall?.customerName ??
          snapshot.customerName ??
          softphone.activeCustomerName;
        const callUuid = queueCall?.callUuid ?? queueUniqueId;
        const callReference = queueCall?.service ?? snapshot.callReference ?? null;

        softphone.setActiveCustomerContext({
          customerId,
          customerName,
          callerNumber: normalizedCaller ?? null,
          matchType: queueCall?.matchType ?? null,
        });
        setCallMetadata({
          customerId: customerId ?? null,
          customerName: customerName ?? null,
          callerNumber: normalizedCaller ?? null,
          callReference,
          callUuid,
          callId: queueCall?.callId ?? queueUniqueId,
        });
        navigate(
          buildCallerDetailsUrl({
            customerId,
            callReference,
            callNumber: normalizedCaller,
            callUuid,
            callMode: "active",
          })
        );

        if (!customerId) return;
        appendCallActivity({
          customerId,
          message: "Call answered",
          diff: [
            createDiffField("Call Status", "Ringing", "Answered"),
            createDiffField("Caller Number", "", normalizedCaller ?? ""),
            createDiffField("Call Reference", "", callReference ?? ""),
          ],
        });
      },
      setCallMetadata,
      answerIncomingCall: async (incomingId: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        const incoming = softphone.screenedIncomingCalls.find(
          (callItem) => callItem.id === incomingId
        );
        await softphone.answerIncomingCall(incomingId);
        const targetCustomerId =
          incoming?.customerId ??
          stateSnapshotRef.current.customerId ??
          softphone.activeCustomerId;
        if (!targetCustomerId) return;
        appendCallActivity({
          customerId: targetCustomerId,
          message: "Call answered",
          diff: [
            createDiffField("Call Status", "Ringing", "Answered"),
            createDiffField(
              "Caller Number",
              "",
              incoming?.callerNumber ?? stateSnapshotRef.current.callerNumber ?? ""
            ),
            createDiffField(
              "Call Reference",
              "",
              incoming?.service ?? stateSnapshotRef.current.callReference ?? ""
            ),
          ],
        });
      },
      declineIncomingCall: async (incomingId: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await softphone.declineIncomingCall(incomingId);
      },
      holdIncomingCall: async (incomingId: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        const incoming = softphone.screenedIncomingCalls.find(
          (callItem) => callItem.id === incomingId
        );
        await softphone.holdIncomingCall(incomingId);
        if (!incoming?.customerId) return;
        appendCallActivity({
          customerId: incoming.customerId,
          message: "Incoming call placed on hold",
          diff: [
            createDiffField("Call Status", "Ringing", "On Hold"),
            createDiffField("Caller Number", "", incoming.callerNumber ?? ""),
          ],
        });
      },
      dismissIncomingCall: softphone.dismissIncomingCall,
      resumeHeldCall: async (heldId: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        const heldCall = softphone.heldCalls.find((entry) => entry.id === heldId);
        await softphone.resumeHeldCall(heldId);
        if (!heldCall?.customerId || heldCall.holdSource !== "incoming-call") {
          return;
        }
        appendCallActivity({
          customerId: heldCall.customerId,
          message: "Call answered",
          diff: [
            createDiffField("Call Status", "Ringing", "Answered"),
            createDiffField("Caller Number", "", heldCall.callerNumber ?? ""),
            createDiffField("Call Reference", "", heldCall.callReference ?? ""),
          ],
        });
      },
      startOutboundCustomerCall: async (input) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        await softphone.startOutboundCustomerCall(input);
        appendCallActivity({
          customerId: input.customerId,
          message: `Outbound call started to ${input.targetNumber}`,
          diff: [
            createDiffField("Call Direction", "", "Outbound"),
            createDiffField("Caller Number", "", input.targetNumber),
            createDiffField("Call Reference", "", input.callReference ?? ""),
          ],
        });
      },
      setActiveCustomerContext: softphone.setActiveCustomerContext,
      refreshCustomerDirectory: softphone.refreshCustomerDirectory,
      supervise: async (targetExtension, uniqueId, mode, options) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await supervise(targetExtension, uniqueId, mode, options);
      },
      inviteOperator: async (colleagueExtension, uniqueId) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }
        await inviteOperator(colleagueExtension, uniqueId);
      },
      pickUpQueueCall: async (uniqueId: string) => {
        if (!sipTabCoordinator.isLeader) {
          requestPrimaryCallingTab();
          return;
        }

        if (
          !connectionHealth.canTakeCalls &&
          !sipTabCoordinator.canRequestLeader
        ) {
          reportCallError(
            "Connection unavailable — wait until dashboard and SIP are both connected."
          );
          return;
        }
        await callQueue.pickUpQueueCall(uniqueId);
      },
    }),
    [
      answer,
      answerWithSipOwnership,
      appendCallActivity,
      call,
      callQueue.pickUpQueueCall,
      canAcceptIncomingAcrossTabs,
      canAcceptQueueAcrossTabs,
      connectionHealth.canTakeCalls,
      reportCallError,
      requestPrimaryCallingTab,
      dismissInvite,
      hangup,
      hold,
      sendDtmf,
      joinConferenceRoom,
      mute,
      reconnect,
      disconnect,
      removeParticipant,
      rejectPendingInvitation,
      dismissCallTakenNotice,
      dismissBargePromotionNotice,
      dismissBargeTransferNotice,
      clearCallError,
      canAcceptIncomingCall,
      canAcceptQueueCall,
      resolveAnswerIncomingId,
      navigate,
      screenedQueueableCalls,
      setCallMetadata,
      supervise,
      inviteOperator,
      softphone.activeCustomerId,
      softphone.activeCustomerName,
      softphone.answerIncomingCall,
      softphone.declineIncomingCall,
      softphone.dismissIncomingCall,
      softphone.heldCalls,
      softphone.screenedIncomingCalls,
      softphone.holdIncomingCall,
      softphone.refreshCustomerDirectory,
      softphone.resumeHeldCall,
      softphone.setActiveCustomerContext,
      softphone.startOutboundCustomerCall,
      sipTabCoordinator.canRequestLeader,
      sipTabCoordinator.isLeader,
      stateSnapshotRef,
      startConferenceWithAgents,
      transferBlind,
      transferToAgent,
      swap,
      unhold,
      unmute,
    ]
  );

  const value = React.useMemo<CallContextValue>(
    () => ({
      state,
      actions,
      connectionHealth,
      isManualPhoneOffline: manualDisconnect,
      screenedIncomingCalls: softphone.screenedIncomingCalls,
      actionableIncomingCalls,
      screenedQueueableCalls,
      alarmSignals: state.alarmSignals,
      heldCalls: softphone.heldCalls,
      answeredCalls: state.answeredCalls,
      activeCustomerId: softphone.activeCustomerId,
      activeCustomerName: softphone.activeCustomerName,
      activeCustomerNumber: softphone.activeCustomerNumber,
      activeMatchType: softphone.activeMatchType,
      directoryLoading: softphone.directoryLoading,
      customers: softphone.customers,
      resolutionContext: callResolution.resolutionContext,
      resolutionModalOpen: callResolution.resolutionModalOpen,
      followUpModalOpen: callResolution.followUpModalOpen,
      pendingResolution: callResolution.pendingResolution,
      resolutionError: callResolution.resolutionError,
      resolutionActions: callResolution.resolutionActions,
      buildFollowUpTitle: callResolution.buildFollowUpTitle,
    }),
    [
      actions,
      connectionHealth,
      manualDisconnect,
      state,
      softphone.screenedIncomingCalls,
      actionableIncomingCalls,
      screenedQueueableCalls,
      callQueue.pickUpQueueCall,
      state.alarmSignals,
      softphone.heldCalls,
      state.answeredCalls,
      softphone.activeCustomerId,
      softphone.activeCustomerName,
      softphone.activeCustomerNumber,
      softphone.activeMatchType,
      softphone.directoryLoading,
      softphone.customers,
      callResolution.resolutionContext,
      callResolution.resolutionModalOpen,
      callResolution.followUpModalOpen,
      callResolution.pendingResolution,
      callResolution.resolutionError,
      callResolution.resolutionActions,
      callResolution.buildFollowUpTitle,
    ]
  );

  const isAnswerLockoutActive =
    state.isOnHold ||
    softphone.heldCalls.length > 0 ||
    state.backendHoldLockoutActive;
  const isSipAcceptBlocked =
    sipTabCoordinator.isLeader
      ? state.sipStatus !== "registered" && actionableIncomingCalls.length === 0
      : !sipTabCoordinator.canRequestLeader;
  const isConnectionAcceptBlocked =
    !manualDisconnect &&
    (sipTabCoordinator.isLeader
      ? !connectionHealth.canTakeCalls
      : !sipTabCoordinator.canRequestLeader);
  const isAcceptBlocked =
    isAnswerLockoutActive || isSipAcceptBlocked || isConnectionAcceptBlocked;
  const resolvedHoldLockoutMessage =
    state.holdLockoutMessage?.trim() || HOLD_LOCKOUT_MESSAGE;
  const resolvedAcceptDisabledMessage = isAnswerLockoutActive
    ? resolvedHoldLockoutMessage
    : isConnectionAcceptBlocked
      ? connectionHealth.status === "reconnecting"
        ? "Reconnecting — wait for dashboard and SIP to reconnect."
        : "Connection unavailable — dashboard and SIP must both be connected."
      : isSipAcceptBlocked
        ? SIP_NOT_REGISTERED_MESSAGE
        : undefined;

  React.useEffect(() => {
    if (state.callTakenNotices.length === 0) return;
    const timers = state.callTakenNotices.map((notice) =>
      window.setTimeout(() => dismissCallTakenNotice(notice.id), 8000)
    );
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [dismissCallTakenNotice, state.callTakenNotices]);

  React.useEffect(() => {
    if (!state.lastError) return;

    const timer = window.setTimeout(() => clearCallError(), 20000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [clearCallError, state.lastError]);

  React.useEffect(() => {
    registerAidCallDebug(() => {
      const sipCtx = sipRef.current?.getConnectLoggingContext();
      const registerReject = sipRef.current?.getLastRegisterReject();
      return {
        version: packageJson.version,
        isAuthenticated,
        role: user?.role ?? null,
        effectiveRole,
        permissionLevel: readUserPermissionLevel(user),
        userOptedOnline: userOptedOnlineRef.current,
        manualDisconnect,
        sipTabRole: sipTabCoordinator.isLeader ? "leader" : "follower",
        canRequestSipLeader: sipTabCoordinator.canRequestLeader,
        sipStatus: stateSnapshotRef.current.sipStatus,
        socketStatus: stateSnapshotRef.current.socketStatus,
        lastError: stateSnapshotRef.current.lastError ?? null,
        extension: sipIdentityRef.current.extension,
        sipUsername: sipIdentityRef.current.sipUsername,
        socketId: socketRef.current?.getSocketId() ?? null,
        sipTransportState: sipRef.current?.getTransportState() ?? null,
        sipRegistererState: sipRef.current?.getRegistererState() ?? null,
        sipWssUrl: sipCtx?.sipWssUrl ?? null,
        sipDomain: sipCtx?.sipDomain ?? null,
        sipUri: sipCtx
          ? `sip:${sipCtx.username}@${sipCtx.sipDomain}`
          : null,
        sipAuthUsername: sipCtx?.authUsername ?? sipCtx?.username ?? null,
        sipHasPassword: Boolean(sipCtx?.password),
        sipLastRegisterRejectStatusCode: registerReject?.statusCode ?? null,
        sipLastRegisterRejectReason: registerReject?.reasonPhrase ?? null,
      };
    });

    return () => {
      unregisterAidCallDebug();
    };
  }, [
    effectiveRole,
    isAuthenticated,
    manualDisconnect,
    sipTabCoordinator.canRequestLeader,
    sipTabCoordinator.isLeader,
    user?.id,
    user?.role,
  ]);

  React.useEffect(() => {
    registerAidCallConnectionHealthDebug({
      getSnapshot: () => connectionHealthSnapshotRef.current,
      recordFlap: (eventType) => {
        connectionHealthRecordFlapRef.current(eventType);
      },
    });

    return () => {
      unregisterAidCallConnectionHealthDebug();
    };
  }, []);

  React.useEffect(() => {
    if (isAcceptBlocked) return;
    if (state.holdLockoutMessage == null) return;
    updateState({ holdLockoutMessage: null });
  }, [isAcceptBlocked, state.holdLockoutMessage, updateState]);

  return (
    <CallContext.Provider value={value}>
      {children}
      <CallUiShell
        audioRef={audioRef}
        pendingInvite={state.pendingInvite}
        callDirection={state.callDirection}
        callStatus={state.callStatus}
        isOnDashboardRoute={softphone.isOnDashboardRoute}
        incomingToastCalls={actionableIncomingCalls}
        callTakenNotices={state.callTakenNotices}
        onDismissCallTakenNotice={dismissCallTakenNotice}
        answerLockoutActive={isAcceptBlocked}
        answerLockoutMessage={resolvedAcceptDisabledMessage}
        onDismissInvite={dismissInvite}
        onJoinConferenceRoom={actions.joinConferenceRoom}
        onAnswerIncomingCall={actions.answerIncomingCall}
        onDeclineIncomingCall={actions.declineIncomingCall}
        onHoldIncomingCall={actions.holdIncomingCall}
        resolutionModalOpen={callResolution.resolutionModalOpen}
        followUpModalOpen={callResolution.followUpModalOpen}
        pendingResolution={callResolution.pendingResolution}
        resolutionContext={callResolution.resolutionContext}
        resolutionError={callResolution.resolutionError}
        buildFollowUpTitle={callResolution.buildFollowUpTitle}
        onDeferResolution={callResolution.resolutionActions.deferResolution}
        onDeferResolutionAndNavigateDashboard={
          callResolution.resolutionActions.deferResolutionAndNavigateDashboard
        }
        onSubmitResolution={callResolution.resolutionActions.submitResolution}
        onSubmitFollowUpAndFinalize={
          callResolution.resolutionActions.submitFollowUpAndFinalize
        }
      />
    </CallContext.Provider>
  );
}

export function useCall() {
  const ctx = React.useContext(CallContext);
  if (!ctx) {
    throw new Error("useCall must be used within CallProvider");
  }
  return ctx;
}

if (import.meta.hot && typeof window !== "undefined") {
  import.meta.hot.dispose(() => {
    disconnectActiveSipTransport();
    void stopActiveSipPhone();
    // Do not force reload here — during a live call beforeunload blocks it and
    // leaves SIP torn down while React is in a broken HMR state.
  });
}
